import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Legend,
} from 'recharts';
import {
  TrendingUp, TrendingDown, AlertTriangle, Lightbulb, Calculator, Sparkles,
  BarChart3, Wallet, Zap, RefreshCw,
} from 'lucide-react';

const fmt0 = (v) => `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmt = (v) => `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const HEALTH_BG = {
  healthy: 'bg-emerald-50 border-emerald-200',
  tight: 'bg-amber-50 border-amber-200',
  critical: 'bg-rose-50 border-rose-200',
};
const HEALTH_DOT = {
  healthy: 'bg-emerald-500', tight: 'bg-amber-500', critical: 'bg-rose-500',
};
const LEVEL = {
  danger: 'bg-rose-50 border-rose-200 text-rose-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  info: 'bg-blue-50 border-blue-200 text-blue-900',
};
const LEVEL_ICON = {
  danger: AlertTriangle, warning: AlertTriangle, success: TrendingUp, info: Lightbulb,
};

export default function CashFlowForecastTab({ BACKEND_URL }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);
  const [extraEmi, setExtraEmi] = useState(0);
  const [revenuePct, setRevenuePct] = useState(0);
  const [extraSavings, setExtraSavings] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        months: String(months),
        extra_home_loan_emi: String(extraEmi || 0),
        revenue_change_pct: String(revenuePct || 0),
        extra_savings_per_month: String(extraSavings || 0),
      });
      const res = await fetch(`${BACKEND_URL}/api/super-admin/financials/cash-flow-forecast?${params}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [BACKEND_URL, months, extraEmi, revenuePct, extraSavings]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) {
    return <Card className="p-6"><div className="animate-pulse h-48 bg-slate-100 rounded" /></Card>;
  }

  const { forecast, averages, starting_cash, scenario, home_loan_impact, insights, personal } = data;

  const annualWorkshop = forecast.reduce((s, m) => s + m.workshop_net_cash, 0);
  const annualCombined = forecast.reduce((s, m) => s + m.combined_net_cash, 0);
  const lowestCum = forecast.reduce((min, m) =>
    m.cumulative_combined_cash < min ? m.cumulative_combined_cash : min, starting_cash);
  const finalCash = forecast[forecast.length - 1]?.cumulative_combined_cash ?? starting_cash;

  const chartData = forecast.map(f => ({
    label: f.label,
    income: Math.round(f.workshop_income),
    expense: Math.round(f.workshop_total_expense + f.gst_owed + f.tax_provision),
    workshopNet: Math.round(f.workshop_net_cash),
    combinedNet: Math.round(f.combined_net_cash),
    cumulative: Math.round(f.cumulative_combined_cash),
  }));

  return (
    <div className="space-y-6" data-testid="forecast-tab">
      {/* Hero */}
      <Card className={`p-6 border-2 ${annualCombined >= 0 ? 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50' : 'border-rose-300 bg-gradient-to-br from-rose-50 to-red-50'}`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className={`text-xs uppercase tracking-wider font-semibold ${annualCombined >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
              {months}-Month Cash Flow Forecast {annualCombined >= 0 ? '(positive)' : '(deficit)'}
            </p>
            <p className={`text-5xl font-black mt-1 ${annualCombined >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
              {annualCombined < 0 && '−'}{fmt0(Math.abs(annualCombined))}
            </p>
            <p className="text-xs text-slate-600 mt-1">
              Combined business + personal · Workshop alone: <strong className={annualWorkshop >= 0 ? 'text-emerald-700' : 'text-rose-700'}>
                {annualWorkshop < 0 && '−'}{fmt0(Math.abs(annualWorkshop))}
              </strong>
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <Pill label="Starting cash" value={starting_cash} />
            <Pill label="Lowest cumulative" value={lowestCum} danger={lowestCum < 0} />
            <Pill label={`After ${months} mo`} value={finalCash} positive={finalCash >= 0} />
          </div>
        </div>
      </Card>

      {/* What-if controls */}
      <Card className="p-5 border-l-4 border-l-purple-500" data-testid="forecast-scenarios">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="h-5 w-5 text-purple-600" />
          <div>
            <h3 className="text-base font-semibold text-slate-900">Stress Test / What-If</h3>
            <p className="text-xs text-slate-500">Tweak the knobs and watch the forecast respond instantly.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Forecast horizon</Label>
            <Input type="number" value={months} onChange={(e) => setMonths(Math.max(3, Math.min(24, parseInt(e.target.value) || 12)))} min={3} max={24} data-testid="forecast-months-input" />
            <p className="text-[10px] text-slate-500 mt-1">3–24 months</p>
          </div>
          <div>
            <Label className="text-xs">Extra home-loan EMI / month ($)</Label>
            <Input type="number" value={extraEmi} onChange={(e) => setExtraEmi(parseFloat(e.target.value) || 0)} min={0} step={50} data-testid="forecast-extra-emi" />
            <p className="text-[10px] text-slate-500 mt-1">{personal?.has_data ? 'Folded into projection' : 'Add a home loan first'}</p>
          </div>
          <div>
            <Label className="text-xs">Revenue change (%)</Label>
            <Input type="number" value={revenuePct} onChange={(e) => setRevenuePct(parseFloat(e.target.value) || 0)} min={-100} max={100} step={5} data-testid="forecast-revenue-pct" />
            <p className="text-[10px] text-slate-500 mt-1">Negative = downturn scenario</p>
          </div>
          <div>
            <Label className="text-xs">Extra savings buffer / month ($)</Label>
            <Input type="number" value={extraSavings} onChange={(e) => setExtraSavings(parseFloat(e.target.value) || 0)} min={0} step={100} data-testid="forecast-extra-savings" />
            <p className="text-[10px] text-slate-500 mt-1">Sets aside cash each month</p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <span className="bg-slate-100 px-2 py-1 rounded">
              Per working day: <strong>{fmt(averages.per_day_income)}</strong> income · <strong>{fmt(averages.per_day_variable_expense)}</strong> variable
            </span>
            <span className="bg-slate-100 px-2 py-1 rounded">
              Based on <strong>{averages.active_working_days}</strong> day{averages.active_working_days === 1 ? '' : 's'} of entries
            </span>
            <span className="bg-slate-100 px-2 py-1 rounded">Recurring fixed: <strong>{fmt(averages.monthly_recurring_fixed)}/mo</strong></span>
          </div>
          <Button variant="outline" size="sm" onClick={() => { setExtraEmi(0); setRevenuePct(0); setExtraSavings(0); }} data-testid="forecast-reset-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reset
          </Button>
        </div>
      </Card>

      {/* Chart */}
      <Card className="p-5" data-testid="forecast-chart">
        <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-indigo-600" />
          Monthly cash flow + cumulative trajectory
        </h3>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip
              formatter={(v, name) => [fmt0(v), name]}
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
            <Bar dataKey="income" name="Income" fill="#10b981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="expense" name="Expense + GST + Tax" fill="#f43f5e" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="cumulative" name="Cumulative cash" stroke="#6366f1" strokeWidth={3} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="combinedNet" name="Combined net" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      {/* Home loan impact */}
      {home_loan_impact && (
        <Card className="p-5 border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="home-loan-impact">
          <div className="flex items-start gap-3">
            <Sparkles className="h-6 w-6 text-emerald-600 mt-1" />
            <div className="flex-1">
              <h4 className="text-base font-semibold text-emerald-900">
                Adding ${home_loan_impact.extra_per_month.toLocaleString()}/month to {home_loan_impact.name}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <Stat title="New EMI" value={fmt0(home_loan_impact.new_emi)} sub={`vs ${fmt0(home_loan_impact.base_emi)} base`} />
                <Stat title="Years saved" value={`${home_loan_impact.years_saved}y`} sub={`${Math.round(home_loan_impact.months_saved)} months`} pos />
                <Stat title="Interest saved" value={fmt0(home_loan_impact.interest_saved)} sub="over loan life" pos />
                <Stat title="Extra commitment" value={fmt0(home_loan_impact.extra_per_month * 12)} sub="per year" />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Insights */}
      {insights && insights.length > 0 && (
        <div className="space-y-2" data-testid="forecast-insights">
          {insights.map((ins, i) => {
            const Icon = LEVEL_ICON[ins.level] || Lightbulb;
            return (
              <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border-l-4 ${LEVEL[ins.level] || LEVEL.info}`}>
                <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold">{ins.title}</p>
                  <p className="text-xs mt-0.5 opacity-90">{ins.message}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Month-by-month table */}
      <Card className="p-5" data-testid="forecast-table">
        <h3 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Calculator className="h-5 w-5 text-indigo-600" />
          Month-by-month detail
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left p-2 sticky left-0 bg-slate-50">Month</th>
                <th className="text-right p-2">Income</th>
                <th className="text-right p-2">Variable</th>
                <th className="text-right p-2">Fixed</th>
                <th className="text-right p-2">GST</th>
                <th className="text-right p-2">Tax</th>
                <th className="text-right p-2">Workshop net</th>
                <th className="text-right p-2">Personal net</th>
                <th className="text-right p-2 font-bold">Combined</th>
                <th className="text-right p-2 font-bold">Cumulative</th>
                <th className="text-center p-2">Health</th>
              </tr>
            </thead>
            <tbody>
              {forecast.map((m) => (
                <tr key={`${m.year}-${m.month}`} className={`border-t hover:bg-slate-50 ${HEALTH_BG[m.health]}`}>
                  <td className="p-2 font-semibold sticky left-0 bg-inherit">{m.label}</td>
                  <td className="text-right p-2 text-emerald-700">{fmt0(m.workshop_income)}</td>
                  <td className="text-right p-2 text-rose-700">−{fmt0(m.workshop_var_expense)}</td>
                  <td className="text-right p-2 text-rose-700">−{fmt0(m.workshop_fixed_expense)}</td>
                  <td className="text-right p-2 text-amber-700">−{fmt0(m.gst_owed)}</td>
                  <td className="text-right p-2 text-slate-600">−{fmt0(m.tax_provision)}</td>
                  <td className={`text-right p-2 font-semibold ${m.workshop_net_cash >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {m.workshop_net_cash < 0 && '−'}{fmt0(Math.abs(m.workshop_net_cash))}
                  </td>
                  <td className={`text-right p-2 ${m.personal_net_cash >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {m.personal_net_cash < 0 && '−'}{fmt0(Math.abs(m.personal_net_cash))}
                  </td>
                  <td className={`text-right p-2 font-bold ${m.combined_net_cash >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>
                    {m.combined_net_cash < 0 && '−'}{fmt0(Math.abs(m.combined_net_cash))}
                  </td>
                  <td className={`text-right p-2 font-bold ${m.cumulative_combined_cash >= 0 ? 'text-indigo-800' : 'text-rose-800'}`}>
                    {m.cumulative_combined_cash < 0 && '−'}{fmt0(Math.abs(m.cumulative_combined_cash))}
                  </td>
                  <td className="text-center p-2">
                    <span className={`inline-block h-2 w-2 rounded-full ${HEALTH_DOT[m.health]}`} title={m.health} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          Projection uses your <strong>per-working-day</strong> rate (averaged over days with entries in the trailing window) × the actual working days in each forecast month, plus your recurring fixed expenses (auto-pulled wages included). GST estimated at 1/11 of net income; tax provisioned at 25% of pre-tax profit.
        </p>
      </Card>
    </div>
  );
}

function Pill({ label, value, danger, positive }) {
  const cls = danger ? 'text-rose-900' : positive ? 'text-emerald-900' : 'text-slate-900';
  return (
    <div className="text-center">
      <p className="text-[10px] uppercase tracking-wider text-slate-600">{label}</p>
      <p className={`text-lg font-bold ${cls}`}>
        {value < 0 && '−'}{fmt0(Math.abs(value))}
      </p>
    </div>
  );
}

function Stat({ title, value, sub, pos }) {
  return (
    <div className="bg-white/70 p-3 rounded-lg border border-emerald-200">
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{title}</p>
      <p className={`text-xl font-black ${pos ? 'text-emerald-900' : 'text-slate-900'}`}>{value}</p>
      <p className="text-[10px] text-slate-500">{sub}</p>
    </div>
  );
}
